"use strict";

var _interopRequireDefault = require("@babel/runtime-corejs3/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _forEach = _interopRequireDefault(require("lodash/forEach"));
var _get = _interopRequireDefault(require("lodash/get"));
var _isNil = _interopRequireDefault(require("lodash/isNil"));
var _uuid = require("uuid");
var _optimizelySdk = _interopRequireDefault(require("@optimizely/optimizely-sdk"));
var _environment = _interopRequireDefault(require("../environment/environment"));
var _storage = _interopRequireDefault(require("../storage/storage"));
var _url = _interopRequireDefault(require("../url/url"));
/* eslint-disable no-underscore-dangle */

const IS_BROWSER = _environment.default.isBrowser();
const FEATURE_VARIABLE_TYPES = ['Boolean', 'Double', 'Integer', 'String', 'Json']; // See https://docs.developers.optimizely.com/full-stack/docs/get-feature-variable-javascript-node
const _segmentExperiments = []; // experiment and variation ids for reporting to Segment
let _client = null;
let _whiteListId = null;
let _idCookieName = null;
let _variationsCookieName = null;
let _serverCookies = {};
let _userAttributes = {};

/**
 * Utilities for working with Optimizely.
 * @module OptimizelyUtils
 */
const OptimizelyUtils = {
  /**
   * @enum {String}
   */
  LOG_LEVEL: _optimizelySdk.default.enums.LOG_LEVEL,
  /**
   * Initialize the Optimizely client with a datafile
   * @static
   * @function initialize
   * @param {Object} datafile - data content provided by Optimizely
   * @param {Object} [options] - Options to pass in.
   * @param {Boolean} [options.isBot] - value to determine if entire block should be skipped
   * @param {Boolean} [options.isDev] - value to determine default logger used
   * @param {Function} [options.loadMechanism] - async fetcher used to get datafile
   * @param {Boolean} [options.isBrowser] - value to determine if reading from server cookie or url params for options
   * @param {Function} [options.onDatafileSuccess] - in the case where a fetch is made for the data file, a function to execute right after
   * @param {Function} [options.onDatafileError] - in the case where a fetch is made for the data file, a function to execute if that fetch fails
   * @param {Function} [options.onActivate] - callback function invoked to notify when Optimizely is activated
   * @param {Function} [options.onDecision] - callback function invoked to notify when Optimizely makes a decision
   * @param {string} [options.logLevel] - The level to set Optimizely's logger to. See https://docs.developers.optimizely.com/full-stack/docs/customize-logger-javascript#section-log-levels
   * @param {string} [options.idCookieName='sh-experiment-id'] - The name of the id cookie
   * @param {string} [options.variationsCookieName='sh-experiment-variations'] - The name of the variations cookie
   * @param {string} [options.serverCookies={}] - Cookies that originated from a server request in SSR environments
   * @return {Object} Returns the Optimizely client and the datafile (the one passed in, or the newly fetched one)
   */
  async initialize(datafile, {
    isBot = false,
    isDev = false,
    isBrowser = true,
    logLevel = null,
    serverCookies = {},
    loadMechanism = null,
    onDatafileError = null,
    onDatafileSuccess = null,
    idCookieName = 'sh-experiment-id',
    variationsCookieName = 'sh-experiment-variations',
    optimizelyDataFileUrl = '/experiments/optimizely-datafile/',
    onActivate = null,
    onDecision = null
  } = {}) {
    let df = datafile;
    let sCookies = serverCookies;
    let logLvl = logLevel;
    if (isBot) {
      return Promise.resolve({
        df
      });
    }
    if (!df && !loadMechanism) {
      throw new Error(`Missing required argument "datafile". No "loadMechanism" supplied so wont try and make the fetch.`);
    }
    if (!df && loadMechanism) {
      // load the datafile if it wasn't already loaded on the server
      try {
        const {
          data
        } = await loadMechanism(optimizelyDataFileUrl);
        df = data;
        if (onDatafileSuccess) {
          onDatafileSuccess();
        }
      } catch (error) {
        if (onDatafileError) {
          return onDatafileError(error);
        }
      }
    }

    // actual server cookies need to be JSON.parsed
    const experimentIdCookie = (0, _get.default)(sCookies, idCookieName, null);
    const variationsCookie = (0, _get.default)(sCookies, variationsCookieName);
    if (isBrowser) {
      sCookies = {
        [idCookieName]: experimentIdCookie,
        [variationsCookieName]: variationsCookie
      };
      if ((0, _get.default)(window, 'location.search', false)) {
        const {
          optimizely_x: optimizelyX,
          // e.g. whitelist IDs
          optimizely_log: optimizelyLog // e.g. INFO, DEBUG, ERROR
        } = _url.default.parseParams(window.location.search);
        if (optimizelyX) {
          _whiteListId = optimizelyX;
        }
        if (optimizelyLog) {
          logLvl = optimizelyLog;
        }
      }
    } else {
      let parsedIdCookie = null;
      let variations = null;
      try {
        parsedIdCookie = JSON.parse(experimentIdCookie);
      } catch (err) {
        parsedIdCookie = experimentIdCookie;
      }
      try {
        variations = JSON.parse(variationsCookie);
      } catch (err) {
        variations = variationsCookie || null;
      }
      sCookies = {
        [idCookieName]: parsedIdCookie,
        [variationsCookieName]: variations
      };
    }
    _idCookieName = idCookieName;
    _variationsCookieName = variationsCookieName;
    if (sCookies) {
      _serverCookies = sCookies;

      // set browser cookies that may have originated on the server side
      if (IS_BROWSER) {
        this.createIdCookie(_serverCookies[_idCookieName]);
        this.updateVariationsCookie(_serverCookies[_variationsCookieName]);
      }
    }
    if (!logLvl && isDev) {
      logLvl = 'ERROR';
    }
    _client = _optimizelySdk.default.createInstance({
      datafile: df,
      logger: _optimizelySdk.default.logging.createLogger({
        logLevel: OptimizelyUtils.LOG_LEVEL[logLvl],
        logToConsole: Boolean(logLvl)
      })
    });
    if (onActivate) {
      _client.notificationCenter.addNotificationListener(_optimizelySdk.default.enums.NOTIFICATION_TYPES.ACTIVATE, payload => {
        onActivate(payload);
      });
    }
    if (onDecision) {
      _client.notificationCenter.addNotificationListener(_optimizelySdk.default.enums.NOTIFICATION_TYPES.DECISION, payload => {
        onDecision(payload);
      });
    }

    // https://segment.com/docs/destinations/optimizely-web/#optimizely-full-stack-javascript
    // See section titled Optimizely Full Stack (JavaScript) - Getting Started
    // This is required for Optimizely/Segment integration
    if (isBrowser) {
      window.optimizelyClientInstance = _client;
    }
    return {
      client: _client,
      datafile: df
    };
  },
  /**
   * Checks whether the Optimizely client is initialized.
   * @static
   * @function isInitialized
   * @example OptimizelyUtils.isInitialized();
   * @return {Boolean} - Whether the client is initialized.
   */
  isInitialized() {
    return Boolean(_client);
  },
  /**
   * Sets the logger level for the Optimizely client.
   * @static
   * @function setLogLevel
   * @param {String} [logLevel='ERROR'] - The level to set. Possible values are null (disable logger), 'INFO', 'DEBUG', 'WARN', and 'ERROR'.
   * @example OptimizelyUtils.setLogLevel('INFO');
   * @return {void}
   */
  setLogLevel(logLevel = 'ERROR') {
    _optimizelySdk.default.setLogLevel(logLevel);
  },
  /**
   * Sets the logger the Optimizely client.
   * @static
   * @function setLogger
   * @param {Object} [options=null] - The options to pass to the logger. Passing null will disable logging. See <a href="https://docs.developers.optimizely.com/full-stack/docs/customize-logger-javascript">docs</a>.
   * @example OptimizelyUtils.setLogger();
   * @return {void}
   */
  setLogger(options = null) {
    _optimizelySdk.default.setLogger(options ? _optimizelySdk.default.logging.createLogger(options) : null);
  },
  /**
   * Checks Optimizely to see if the user is bucketed in this experiment
   * Activates the experiment for the current session; an impression will be sent to Optimizely
   * Appends the experiment name and variation name to a cookie (used by analytics and the Reservations API)
   * @static
   * @function activate
   * @param {String} experimentName - Experiment name
   * @example OptimizelyUtils.activate('mw_checkout_value_props') => 'vp_below';
   * @return {String} variationName - For each variation, returns the name of the variation assigned to this user, or 'a'
   */
  activate(experimentName) {
    if (!experimentName) {
      throw new Error(`Missing required argument "experimentName".`);
    }
    if (_client) {
      let variationName;
      if (IS_BROWSER) {
        variationName = _client.activate(experimentName, this.getId(), _userAttributes) || 'a';
      } else {
        variationName = this.getVariation(experimentName);
      }
      this.updateVariationsCookie({
        [experimentName]: variationName
      });
      this.updateSegmentExperiments({
        experimentName,
        variationName,
        type: 'ab-test'
      });
      return variationName;
    }
    return 'a';
  },
  /**
   * Checks if a feature is enabled for a user
   * @static
   * @function isFeatureEnabled
   * @param {String} featureName - The name of the feature
   * @example OptimizelyUtils.isFeatureEnabled('web_canada');
   * @returns {Boolean} isEnabled - Whether or not the feature is enabled for this user
   */
  isFeatureEnabled(featureName) {
    if (!featureName) {
      throw new Error(`Missing required argument "featureName".`);
    }
    if (_client) {
      // There is no option to check isFeatureEnabled (on server-side) without potentially generating an impression
      // Since isFeatureEnabled is used for both feature experiments and rollouts, this call cannot be substituted with getVariation
      const isFeatureEnabled = _client.isFeatureEnabled(featureName, this.getId(), _userAttributes);
      if (isFeatureEnabled) {
        const featureExperiments = this.getExperimentsAndVariationsForFeature(featureName);
        featureExperiments.forEach(experiment => {
          const {
            experimentName,
            variationName
          } = experiment;
          this.updateVariationsCookie({
            [experimentName]: variationName
          });
          this.updateSegmentExperiments({
            ...experiment,
            type: 'feature-test'
          });
        });
      }
      return isFeatureEnabled;
    }
    return false;
  },
  /**
   * Checks Optimizely to see if the user is bucketed in this experiment
   * Does not activate the experiment for the current session; no impression will be sent to Optimizely
   * @static
   * @function getVariation
   * @param {String} experimentName - Name of experiment
   * @example OptimizelyUtils.getVariation('homepage_tabbed_search') => 'tabbed_with_value_props';
   * @return {String} variationName - For each variation, returns the name of the variation assigned to this user, or 'a'
   */
  getVariation(experimentName) {
    if (!experimentName) {
      throw new Error(`Missing required argument "experimentName".`);
    }
    if (_client) {
      return _client.getVariation(experimentName, this.getId(), _userAttributes) || 'a';
    }
    return 'a';
  },
  /**
   * @static
   * @function getFeatureVariable
   * @param {Object} requiredParams - All params are required
   * @param {String} requiredParams.featureName - The name of the feature
   * @param {String} requiredParams.variableName - The name of the feature variable
   * @param {String} requiredParams.type - The type of the feature variable
   * @example OptimizelyUtils.getFeatureVariable({
   *      featureName: 'web_canada',
   *      variableName: 'search_enabled',
   *      type: 'Boolean',
   * });
   * @returns {Double|Integer|String|Boolean|Json} variableValue - The value of the given variable and type for the given feature
   */
  getFeatureVariable(requiredParams) {
    const missingArguments = Object.keys(requiredParams).filter(param => !requiredParams[param]);
    if (missingArguments.length) {
      throw new Error(`Missing required argument(s): ${missingArguments.join(', ')}`);
    }
    if (_client) {
      const {
        featureName,
        variableName,
        type
      } = requiredParams;
      const formattedType = type[0].toUpperCase() + type.substring(1).toLowerCase();
      if (!FEATURE_VARIABLE_TYPES.find(supportedType => supportedType === formattedType)) {
        throw new Error(`Type must be one of: ${FEATURE_VARIABLE_TYPES.join(', ')}. Given type: ${type}`);
      }
      return _client[`getFeatureVariable${/json/i.test(formattedType) ? formattedType.toUpperCase() : formattedType}`](featureName, variableName, this.getId(), _userAttributes);
    }
    return false;
  },
  /**
   * Get values of feature variables for the given feature
   * @static
   * @function getEnabledFeatureVariables
   * @param {String} featureName - Name of the enabled feature
   * @param {Array} featureVariables - Array containing feature variable name and type
   * @example OptimizelyUtils.getEnabledFeatureVariables('filter_sort', [
   *   ['sort_distance', 'Boolean'],
   *   ['sort_price', 'String'],
   *   ['filters', 'Integer'],
   *   ['map', 'Double'],
   * ]);
   * @returns {Object} - Returns dictionary of variable name/value key/value pairs
   */
  getEnabledFeatureVariables(featureName, featureVariables) {
    return featureVariables.reduce((allVariables, [variableName, type]) => ({
      ...allVariables,
      [variableName]: this.getFeatureVariable({
        featureName,
        variableName,
        type
      })
    }), {});
  },
  /**
   * Return experiment names and variations for a given feature
   * @static
   * @function getExperimentsAndVariationsForFeature
   * @param {String} featureName - Name of feature flag
   * @returns {Array} featureExperiments - Array of objects, each containing experiment information
   */
  getExperimentsAndVariationsForFeature(featureName) {
    if (_client) {
      const {
        featuresMap
      } = _client.getOptimizelyConfig();
      const experiments = featuresMap[featureName].experimentsMap;
      const featureExperiments = [];
      (0, _forEach.default)(experiments, ({
        id,
        key,
        variationsMap
      }) => {
        featureExperiments.push({
          id,
          experimentName: key,
          variationName: this.getVariation(key)
        });
      });
      return featureExperiments;
    } else {
      return [];
    }
  },
  /**
   * Return the serverCookies object.
   * @static
   * @function getServerCookies
   * @returns {Object} - The cookies from the server request.
   */
  getServerCookies() {
    return _serverCookies;
  },
  /**
   * Return the value of the id cookie (or create new cookie and return its value)
   * @static
   * @function getIdCookie
   * @returns {String} - Either the existing cookie or a new value from a new cookie
   */
  getIdCookie() {
    const shExperimentCookie = IS_BROWSER ? _storage.default.get(_idCookieName, 'cookie') : _serverCookies[_idCookieName];
    return !(0, _isNil.default)(shExperimentCookie) ? shExperimentCookie.toString() : this.createIdCookie();
  },
  /**
   * Create a unique ID and store it in the id cookie
   * @static
   * @function createIdCookie
   * @param {String} id - An ID to set as the id cookie
   * @returns {String} uuid - Creates a unique ID and stories it in the id cookie
   */
  createIdCookie(id) {
    const uuid = id || (0, _uuid.v4)();
    if (IS_BROWSER) {
      _storage.default.set(_idCookieName, uuid, 'cookie', 365);
    } else {
      _serverCookies[_idCookieName] = uuid;
    }
    return uuid;
  },
  /**
   * Return user id for use with Optimizely SDK
   * @static
   * @function getId
   * @returns {String} Either the id cookie value or the value passed to the optimizely_x query param
   */
  getId() {
    return _whiteListId || this.getIdCookie();
  },
  /**
   * Return the experiment names and variations stored in the variations cookie
   * @static
   * @function getVariationsCookie
   * @returns {Object} keys are experiment names and corresponding values are variation names.
   */
  getVariationsCookie() {
    const shVariationsCookie = IS_BROWSER ? _storage.default.get(_variationsCookieName, 'cookie') || {} : _serverCookies[_variationsCookieName];
    return shVariationsCookie;
  },
  /**
   * Update the experiment names and variations stored in the variations cookie
   * @static
   * @function updateVariationsCookie
   * @param {Object} newVariations - New object where key is experiment name corresponding value is variation name
   * @example updateVariationsCookie({'mw_checkout_value_props': 'vp_below'});
   * @returns {void}
   */
  updateVariationsCookie(newVariations) {
    const currentVariations = this.getVariationsCookie();
    const updatedVariations = {
      ...currentVariations,
      ...newVariations
    };
    if (IS_BROWSER) {
      _storage.default.set(_variationsCookieName, updatedVariations, 'cookie', 365);
    } else {
      _serverCookies[_variationsCookieName] = updatedVariations;
    }
  },
  /**
   * Clear the experiment names and variations stored in the variations cookie
   * @static
   * @function clearVariationsCookie
   * @returns {void}
   */
  clearVariationsCookie() {
    if (IS_BROWSER) {
      _storage.default.set(_variationsCookieName, {}, 'cookie', 365);
    } else {
      _serverCookies[_variationsCookieName] = {};
    }
  },
  /**
   * Returns experiment data formatted specifically for Segment
   * @static
   * @function getSegmentExperiments
   * @returns {Array} - Array with object for each active experiment
   */
  getSegmentExperiments() {
    return _segmentExperiments;
  },
  /**
   * Update experiment data formatted specifically for Segment
   * @static
   * @function getSegmentExperiments
   * @param {String} experimentName - Name of experiment
   * @param {String} variationName - Name of experiment variation
   * @param {String} type - 'ab-test' | 'feature-test'
   * @returns {void}
   */
  updateSegmentExperiments({
    experimentName,
    variationName,
    type
  } = {}) {
    if (_client) {
      const {
        experimentsMap
      } = _client.getOptimizelyConfig();
      if (variationName !== 'a') {
        const variationObj = experimentsMap[experimentName].variationsMap[variationName];

        /* eslint-disable camelcase */
        _segmentExperiments.push({
          experiment_name: experimentName,
          experiment_id: experimentsMap[experimentName].id,
          variation_name: variationName,
          variation_id: variationObj.id,
          type
        });
        /* eslint-enable camelcase */
      }
    }
  },
  /**
   * Add attributes to be sent along with requests for feature and experiment values.
   * Attributes must be configured in Optimizely and appear in the datafile to be captured correctly
   * @static
   * @function setUserAttributes
   * @param {Object} userAttributes - Supported attributes are defined in our Optimizely implementation
   * @param {Object} [options] - Options to pass in
   * @param {Object} [options.reset] - Remove any existing attributes before setting new ones
   * @returns {void}
   */
  setUserAttributes(userAttributes, {
    reset = false
  } = {}) {
    _userAttributes = {
      ...(!reset && _userAttributes),
      ...userAttributes
    };
  },
  /**
   * Forces a user into a variation for a given experiment for the lifetime of the Optimizely client
   * @static
   * @param {String} experimentKey - The key of the experiment to set with the forced variation
   * @param {String} [variationKey] - The key of the forced variation. Set the value to null to clear the existing experiment-to-variation mapping. (optional)
   * @function setForcedVariation
   * @returns {Boolean}
   */
  setForcedVariation(experimentKey, variationKey) {
    if (!experimentKey) {
      throw new Error('Missing requirement `experimentKey`.');
    }
    return _client ? _client.setForcedVariation(experimentKey, this.getId(), variationKey) : false;
  },
  /**
   * Returns the forced variation set by setForcedVariation or null if no variation was forced
   * @static
   * @param {String} experimentKey - The key of the experiment to retrieve the forced variation.
   * @function getForcedVariation
   * @returns {String|null} - The variation the user was bucketed into, or null
   */
  getForcedVariation(experimentKey) {
    if (!experimentKey) {
      throw new Error('Missing requirement `experimentKey`.');
    }
    if (_client) {
      return _client.getForcedVariation(experimentKey, this.getId());
    }
    return null;
  },
  /**
   * Clears the associated Optimizely client instance.
   * @static
   * @function clearClient
   * @returns {void}
   */
  clearClient() {
    _client = null;
  }
};
var _default = exports.default = OptimizelyUtils;